import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { api } from "../lib/api";
import { useEnergi } from "../lib/useEnergi";
import { useWargaAktif } from "../lib/presence";
import { FONT, glassVars } from "../components/kota/ui";
import { SideDock } from "../components/kota/SideDock";
import { TOKEN_KEY, Verifikasi } from "../components/kota/PesanSheet";
import { Scene } from "../components/kerja/Scene";
import { KerjaTopBar, LevelChip } from "../components/kerja/KerjaTopBar";
import { MulaiBar } from "../components/kerja/MulaiBar";
import { GantiKerja } from "../components/kerja/GantiKerja";
import { Permainan } from "../components/kerja/Permainan";
import { Sawah } from "../components/kerja/games/Sawah";
import { LemparPaket } from "../components/kerja/games/LemparPaket";
import { Barista } from "../components/kerja/games/Barista";
import { Tukang } from "../components/kerja/games/Tukang";
import { Razia } from "../components/kerja/games/Razia";
import { HasilKerja, Bantuan, MisiKota, MisiChip } from "../components/kerja/KerjaModals";

// Mini game khusus per profesi; yang belum punya memakai kerangka generik `Permainan`
const GAMES = { petani: Sawah, ojol: LemparPaket, barista: Barista, tukang: Tukang, polisi: Razia };

// Halaman Kerja — layar penuh terpisah dari peta (ala Thaloria): bar atas (profesi · energi · bantuan), latar tempat kerja,
// tombol Mulai di dasar. Alur: pilih profesi → Mulai (⚡5) → mini game → hasil koin/XP. Hanya pemegang Kartu Warga.
export default function Kerja() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const kota = sp.get("kota") || "kediri";
  useWargaAktif("kerja");
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [daftar, setDaftar] = useState(null);
  const [dompet, setDompet] = useState(null);
  const [modal, setModal] = useState(null); // ganti | bantuan | hasil | verifikasi | null
  const [shift, setShift] = useState(null);
  const [hasil, setHasil] = useState(null);
  const [misi, setMisi] = useState([]);
  const muatMisi = () => (token ? api.ekonomiMisiSaya(token) : api.ekonomiMisi()).then(setMisi).catch(() => {});
  useEffect(() => { muatMisi(); }, [token]); // eslint-disable-line
  async function ikutMisi(id) { try { await api.misiIkut(token, id); toast.success("Kamu mengikuti misi ini — selesaikan shift untuk memajukan progres"); muatMisi(); } catch (e) { toast.error(e.message); } }
  async function klaimMisi(id) { try { const h = await api.misiKlaim(token, id); toast.success(`Hadiah diklaim: +${h.koin} koin${h.xp ? ` · +${h.xp} XP` : ""}`); muatMisi(); muat(); } catch (e) { toast.error(e.message); } }
  const energi = useEnergi(dompet);

  const muat = useCallback(() => token && api.ekonomiDompet(token).then(setDompet).catch(() => setDompet(null)), [token]);
  useEffect(() => { api.ekonomiProfesi().then(setDaftar).catch(() => setDaftar({ profesi: [] })); }, []);
  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    const t = setInterval(muat, 60000);
    const onVis = () => document.visibilityState === "visible" && muat();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [muat]);
  // Belum punya profesi → langsung tawarkan daftar kerja
  useEffect(() => { if (dompet && !dompet.profesi_aktif && !modal) setModal("ganti"); }, [dompet]); // eslint-disable-line

  const aktifKey = dompet?.profesi_aktif;
  const aktif = daftar?.profesi.find(p => p.key === aktifKey);
  const biaya = daftar?.energi_biaya ?? 5;

  async function pilih(key) {
    try { await api.ekonomiPilihProfesi(token, key); await muat(); setModal(null); } catch (e) { toast.error(e.message); }
  }
  async function jadikanUtama(key) {
    try { await api.ekonomiPilihUtama(token, key); await muat(); toast.success(`${daftar.profesi.find(p => p.key === key)?.nama} jadi pekerjaan utama (bonus ×${daftar.bonus_utama})`); } catch (e) { toast.error(e.message); }
  }
  async function mulai() {
    try { const s = await api.ekonomiMulai(token); setHasil(null); setModal(null); setShift(s); setDompet(d => d && { ...d, energi: s.energi, energi_berikut_detik: d.energi_berikut_detik || d.energi_regen_detik }); }
    catch (e) { toast.error(e.message); muat(); }
  }
  async function selesai(skor) {
    const s = shift; setShift(null);
    try { const h = await api.ekonomiSelesai(token, s.shift_id, skor); setHasil({ ...h, skor }); setModal("hasil"); muat(); muatMisi(); (h.misi_selesai || []).forEach(j => toast.success(`Misi tercapai: ${j}`)); }
    catch (e) { toast.error(e.message); muat(); }
  }

  const mode = !token ? "warga" : !aktif ? "pilih" : (energi.energi ?? 0) < biaya ? "habis" : "mulai";
  const aksiMulai = { warga: () => setModal("verifikasi"), pilih: () => setModal("ganti"), habis: () => {}, mulai }[mode];
  const Game = shift ? GAMES[shift.profesi] || Permainan : null;

  return (
    <div data-testid="kerja-page" style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#cfe6f4", color: "#141414", fontFamily: FONT, ...glassVars(false) }}>
      <Scene profesi={aktifKey || "petani"} idle={!shift} />

      <KerjaTopBar profesi={aktif} utama={aktifKey && dompet?.profesi_utama === aktifKey} energi={energi.energi} energiMaks={dompet?.energi_maks ?? daftar?.energi_maks ?? 50}
        onBack={() => nav(`/kota/${kota}`)} onGanti={() => setModal("ganti")} onBantuan={() => setModal("bantuan")} onProfil={token ? () => nav("/profil") : null} disabled={!token || !!shift} />
      {!shift && <LevelChip data={dompet?.profesi?.[aktifKey]} />}
      {!shift && misi.length > 0 && <MisiChip jumlah={misi.length} onClick={() => setModal("misi")} />}
      {!shift && <SideDock active={null} onOpen={key => key && nav(`/kota/${kota}?buka=${key}`)} />}

      {shift && <Game key={shift.shift_id} shift={shift} onSelesai={selesai} />}
      {!shift && <MulaiBar mode={mode} biaya={biaya} berikut={energi.berikut} onClick={aksiMulai} />}

      {modal === "ganti" && daftar && <GantiKerja daftar={daftar.profesi} dompet={dompet} onPilih={pilih} onUtama={jadikanUtama} onClose={() => setModal(null)} />}
      {modal === "bantuan" && daftar && <Bantuan aturan={daftar} onClose={() => setModal(null)} />}
      {modal === "misi" && <MisiKota misi={misi} token={token} onIkut={ikutMisi} onKlaim={klaimMisi} onWarga={() => setModal("verifikasi")} onClose={() => setModal(null)} />}
      {modal === "hasil" && hasil && <HasilKerja hasil={hasil} biaya={biaya} onLagi={mulai} onTutup={() => setModal(null)} />}
      {modal === "verifikasi" && <Verifikasi cityId={kota} onBack={() => setModal(null)} onClose={() => setModal(null)} />}
      <Toaster theme="light" position="top-center" />
    </div>
  );
}
